import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import StyledDataGrid from '../../../shared/StyledDataGrid';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { MonthlyReturn } from '../../../interfaces/DTO/agent/mimshakGroupBy';
import { mimshakDataService } from '../../../services/mimshakDataService';
import { mimshakDataPreAdviceService } from '../../../services/mimshakDataPreAdviceService';
import { Contact } from '../../../interfaces/DTO/crm/contact';
import { Product } from '../../../interfaces/DTO/mislaka_xml_models';
import { MimshakService } from '../../../services/mimshakService';
import {
  ChartContainer,
  buildChartOptions,
  buildLineDataset,
  finalValueLabelPlugin,
} from '../../../shared/UIParts/chartUtils';
import { formatCurrency, formatIsoDate, formatNumberFloor } from '../../../utils/namesFormater';
import { currencyColumn } from '../../../shared/UIParts/columnsFormatter';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Title, Tooltip, Legend);

interface Props {
  contacts: Contact[];
  productTypes: number[];
  filteredProducts?: Product[];
}

interface RowData extends MonthlyReturn {
  id: string;
  acc_deposit: number;
  acc_return: number;
  acc_return_pct: number | null;
}

type RangeKey = 'YTD' | '6M' | '1Y' | 'ALL';

const MIN_PERIOD = '2025-04-30';

const clampToMin = (date: string | null): string | null =>
  date && date < MIN_PERIOD ? MIN_PERIOD : date;

const getRangeStart = (key: RangeKey): string | null => {
  const now = new Date();
  if (key === 'YTD') return clampToMin(`${now.getFullYear()}-01-01`);
  if (key === '6M') { now.setMonth(now.getMonth() - 6); return clampToMin(now.toISOString().slice(0, 10)); }
  if (key === '1Y') { now.setFullYear(now.getFullYear() - 1); return clampToMin(now.toISOString().slice(0, 10)); }
  return MIN_PERIOD;
};

const getAdjustedProductTypes = (products: Product[]): number[] =>
  [...new Set(products
    .map(p => p.details?.adjusted_product_type)
    .filter((t): t is number => t != null)
  )];

const filterProductsByCategory = (products: Product[], category: 'liquid' | 'pension'): Product[] =>
  products.filter(p => {
    const adjusted = p.details?.adjusted_product_type;
    if (adjusted == null) return false;
    return category === 'liquid'
      ? MimshakService.liquidFundsTypesByAdjustedProductType.includes(adjusted)
      : MimshakService.pensionFundsTypesByAdjustedProductType.includes(adjusted);
  });

const calcMetrics = (data: MonthlyReturn[]): { accReturn: number; pct: number | null } => {
  const accReturn = data.reduce((sum, d) => sum + d.return_amount, 0);
  const lastFunds = data[data.length - 1]?.total_funds_sum ?? 0;
  const base = lastFunds - accReturn;
  const pct = base !== 0 ? Math.round((accReturn / base) * 10000) / 100 : null;
  return { accReturn, pct };
};

type CategoryKey = 'all' | 'liquid' | 'pension';

interface SummaryMetricProps {
  label: string;
  accReturn: number;
  pct: number | null;
  loading: boolean;
  selected: boolean;
  onClick: () => void;
}

const SummaryMetric: React.FC<SummaryMetricProps> = ({ label, accReturn, pct, loading, selected, onClick }) => (
  <Paper
    elevation={selected ? 4 : 2}
    onClick={onClick}
    sx={{
      p: 2, textAlign: 'center', minWidth: 160, cursor: 'pointer',
      border: selected ? '2px solid' : '2px solid transparent',
      borderColor: selected ? 'primary.main' : 'transparent',
      transition: 'all 0.15s',
    }}
  >
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    {loading
      ? <CircularProgress size={20} />
      : <>
          <Typography variant="h6" fontWeight="bold" color={accReturn >= 0 ? 'success.main' : 'error.main'}>
            {formatCurrency(Math.round(accReturn))}
          </Typography>
          {pct != null && (
            <Typography variant="body2" color={pct >= 0 ? 'success.main' : 'error.main'}>
              {pct.toFixed(2)}%
            </Typography>
          )}
        </>
    }
  </Paper>
);

export const MonthlyReturnsGrid: React.FC<Props> = ({ contacts: users, productTypes, filteredProducts }) => {
  const [data, setData] = useState<MonthlyReturn[]>([]);
  const [liquidData, setLiquidData] = useState<MonthlyReturn[]>([]);
  const [pensionData, setPensionData] = useState<MonthlyReturn[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [range, setRange] = useState<RangeKey>('1Y');
  const [category, setCategory] = useState<CategoryKey>('all');

  useEffect(() => {
    console.log('[MonthlyReturnsGrid] useEffect triggered', {
      userIds: users.map(u => u.id),
      productTypes,
      filteredProductsCount: filteredProducts?.length,
      filteredProductsIds: filteredProducts?.map(p => p.details?.adjusted_product_type ?? p.details?.product_type),
    });
    const fetchReturns = async () => {
      try {
        setLoading(true);
        const userIds = users
          .map((user) => user.id)
          .filter((id): id is number => id !== undefined);

        if (!userIds.length || !productTypes.length) return;

        const filteredTypes = filteredProducts?.length
          ? getAdjustedProductTypes(filteredProducts)
          : undefined;

        const liquidTypes = filteredProducts?.length
          ? getAdjustedProductTypes(filterProductsByCategory(filteredProducts, 'liquid'))
          : undefined;

        const pensionTypes = filteredProducts?.length
          ? getAdjustedProductTypes(filterProductsByCategory(filteredProducts, 'pension'))
          : undefined;

        const [allResult, allPreAdvice, liquidResult, liquidPreAdvice, pensionResult, pensionPreAdvice] = await Promise.all([
          mimshakDataService.getMonthlyReturnsForUsers(userIds, productTypes, filteredTypes),
          mimshakDataPreAdviceService.getMonthlyReturnsForUsers(userIds, productTypes, filteredTypes),
          liquidTypes?.length
            ? mimshakDataService.getMonthlyReturnsForUsers(userIds, productTypes, liquidTypes)
            : Promise.resolve([]),
          liquidTypes?.length
            ? mimshakDataPreAdviceService.getMonthlyReturnsForUsers(userIds, productTypes, liquidTypes)
            : Promise.resolve([]),
          pensionTypes?.length
            ? mimshakDataService.getMonthlyReturnsForUsers(userIds, productTypes, pensionTypes)
            : Promise.resolve([]),
          pensionTypes?.length
            ? mimshakDataPreAdviceService.getMonthlyReturnsForUsers(userIds, productTypes, pensionTypes)
            : Promise.resolve([]),
        ]);

        const mergeByPeriod = (a: MonthlyReturn[], b: MonthlyReturn[]): MonthlyReturn[] => {
          const map: Record<string, MonthlyReturn> = {};
          [...a, ...b].forEach((r) => {
            if (!map[r.reported_period]) {
              map[r.reported_period] = { ...r };
            } else {
              map[r.reported_period].total_funds_sum += r.total_funds_sum;
              map[r.reported_period].total_deposit += r.total_deposit;
              map[r.reported_period].total_withdrawal += r.total_withdrawal;
              map[r.reported_period].return_amount += r.return_amount;
            }
          });
          return Object.values(map).sort((a, b) => a.reported_period.localeCompare(b.reported_period));
        };

        setData(mergeByPeriod(allResult, allPreAdvice));
        setLiquidData(mergeByPeriod(liquidResult, liquidPreAdvice));
        setPensionData(mergeByPeriod(pensionResult, pensionPreAdvice));
      } catch (error) {
        console.error('Failed to load monthly returns', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReturns();
  }, [users, productTypes, filteredProducts]);

  const filteredData = useMemo(() => {
    const rangeStart = getRangeStart(range);
    if (!rangeStart) return data;
    return data.filter((d) => d.reported_period >= rangeStart);
  }, [data, range]);

  const filteredLiquidData = useMemo(() => {
    const rangeStart = getRangeStart(range);
    if (!rangeStart) return liquidData;
    return liquidData.filter((d) => d.reported_period >= rangeStart);
  }, [liquidData, range]);

  const filteredPensionData = useMemo(() => {
    const rangeStart = getRangeStart(range);
    if (!rangeStart) return pensionData;
    return pensionData.filter((d) => d.reported_period >= rangeStart);
  }, [pensionData, range]);

  const activeData = category === 'liquid' ? filteredLiquidData : category === 'pension' ? filteredPensionData : filteredData;

  const chartData = useMemo(() => {
    if (!activeData.length) return null;

    const labels = activeData.map((d) => formatIsoDate(d.reported_period));

    let accumulated = 0;
    const accumulatedValues = activeData.map((d) => {
      accumulated += d.return_amount;
      return Math.round(accumulated);
    });

    const lastValue = accumulatedValues[accumulatedValues.length - 1] ?? 0;

    return {
      labels,
      datasets: [
        {
          ...buildLineDataset({
            label: 'תשואה נצברת',
            data: accumulatedValues,
            backgroundColor: 'rgba(33, 150, 243, 0.15)',
            borderColor: 'rgba(33, 150, 243, 1)',
            finalValueLabel: formatNumberFloor(lastValue),
          }),
          fill: true,
          pointBackgroundColor: '#ffffff',
          pointBorderColor: 'rgba(33, 150, 243, 1)',
          pointBorderWidth: 1.5,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  }, [activeData]);

  const chartOptions = useMemo(
    () =>
      buildChartOptions({
        isLineChart: true,
        isStacked: false,
        beginAtZero: false,
        formatValue: formatCurrency,
      }),
    []
  );

  const rows: RowData[] = useMemo(() => {
    let accDeposit = 0;
    let accReturn = 0;
    return activeData.map((item) => {
      accDeposit += item.total_deposit;
      accReturn += item.return_amount;
      const base = item.total_funds_sum - accReturn;
      const acc_return_pct = base !== 0 ? Math.round((accReturn / base) * 10000) / 100 : null;
      return { ...item, id: item.reported_period, acc_deposit: accDeposit, acc_return: Math.round(accReturn), acc_return_pct };
    });
  }, [activeData]);

  const columns: GridColDef<RowData>[] = [
    {
      field: 'reported_period',
      headerName: 'תקופה',
      flex: 1,
      valueFormatter: (value) =>
        new Date(value).toLocaleDateString('he-IL', { month: 'long', year: 'numeric' }),
    },
    { ...currencyColumn('total_funds_sum', 'סה"כ בתיק', 150, true), flex: 1 },
    { ...currencyColumn('return_amount', 'תשואה חודשית', 150, false), flex: 1 },
    { ...currencyColumn('acc_return', 'תשואה מצטברת', 150, true), flex: 1 },
    {
      field: 'acc_return_pct',
      headerName: 'תשואה מצטברת %',
      flex: 1,
      renderCell: (params) =>
        params.value != null ? (
          <span dir="ltr">{params.value.toFixed(2)}%</span>
        ) : null,
    },
    { ...currencyColumn('total_deposit', 'סך הפקדות', 150, false), flex: 1 },
    { ...currencyColumn('acc_deposit', 'הפקדות מצטברות', 150, false), flex: 1 },
    { ...currencyColumn('total_withdrawal', 'סך משיכות', 150, false), flex: 1 },
  ];

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
        תשואה פסיבית נצברת
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          תשואה נטו אחרי דמי ניהול, ללא הפקדות ומשיכות
        </Typography>
        <Stack direction="row" spacing={1}>
          {(['YTD', '6M', '1Y', 'ALL'] as RangeKey[]).map((key) => (
            <Button
              key={key}
              size="small"
              variant={range === key ? 'contained' : 'outlined'}
              onClick={() => setRange(key)}
            >
              {key}
            </Button>
          ))}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        {([
          { label: 'תשואה מצטברת - הכל', data: filteredData, key: 'all' },
          { label: 'תשואה מצטברת - הון', data: filteredLiquidData, key: 'liquid' },
          { label: 'תשואה מצטברת - פנסיה', data: filteredPensionData, key: 'pension' },
        ] as { label: string; data: MonthlyReturn[]; key: CategoryKey }[]).map(({ label, data: d, key }) => {
          const { accReturn, pct } = calcMetrics(d);
          return (
            <SummaryMetric
              key={key}
              label={label}
              accReturn={accReturn}
              pct={pct}
              loading={loading}
              selected={category === key}
              onClick={() => setCategory(key)}
            />
          );
        })}
      </Stack>

      <ChartContainer height={350}>
        {chartData && (
          <Chart
            type="line"
            data={chartData}
            options={chartOptions}
            plugins={[finalValueLabelPlugin]}
          />
        )}
      </ChartContainer>

      <Box sx={{
        mt: 3,
        '& .positive-cell': { color: 'success.main', fontWeight: 'bold' },
        '& .negative-cell': { color: 'error.main', fontWeight: 'bold' },
      }}>
        <StyledDataGrid rows={rows} columns={columns} autoExpand />
      </Box>
    </Box>
  );
};
